import React from 'react'
import { Clock, RefreshCw, CheckCircle, AlertCircle, Activity, Music, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button, Card } from '../ui'
import { cn } from '../../lib/utils'

const ProcessingJobCard = ({ job, retryProcessingJob, formatDate, extractVideoId }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
            case 'processing': return 'bg-blue-500/10 text-blue-400 border-blue-500/30'
            case 'completed': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
            case 'failed': return 'bg-red-500/10 text-red-400 border-red-500/30'
            default: return 'bg-slate-500/10 text-slate-400 border-slate-500/30'
        }
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <Clock className="w-4 h-4" />
            case 'processing': return <RefreshCw className="w-4 h-4 animate-spin" />
            case 'completed': return <CheckCircle className="w-4 h-4" />
            case 'failed': return <AlertCircle className="w-4 h-4" />
            default: return <Activity className="w-4 h-4" />
        }
    }

    return (
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm p-5 hover:bg-white/10 transition-all border-l-4 border-l-primary-500/50 shadow-lg">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                        <div className={cn("flex items-center space-x-2 px-3 py-1 rounded-full border backdrop-blur-md", getStatusColor(job.status))}>
                            {getStatusIcon(job.status)}
                            <span className="text-xs font-bold uppercase tracking-wider">{job.status}</span>
                        </div>
                        <span className="text-xs font-mono text-slate-500 bg-black/20 px-2 py-1 rounded border border-white/5">
                            ID: {job.jobId.slice(0, 8)}
                        </span>
                    </div>

                    <p className="font-bold text-lg text-white mb-1 group-hover:text-primary-300 transition-colors">
                        {job.song?.title || `Processing: ${extractVideoId(job.youtubeUrl)}`}
                    </p>

                    <p className="text-sm text-slate-400 truncate mb-3 font-mono bg-black/10 p-1.5 rounded border border-white/5">{job.youtubeUrl}</p>

                    {job.song?.artist && (
                        <p className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                            <Music className="w-4 h-4 text-primary-400" /> {job.song.artist}
                        </p>
                    )}

                    {job.status === 'processing' && (
                        <div className="mb-4 bg-black/20 p-3 rounded-xl border border-white/5">
                            <div className="flex justify-between text-xs font-bold text-slate-300 mb-2 uppercase tracking-tight">
                                <span>Processing Progress</span>
                                <span className="text-primary-400">{job.progress || 0}%</span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-2.5 overflow-hidden border border-white/5">
                                <div
                                    className="bg-gradient-to-right from-primary-600 to-primary-400 h-full rounded-full transition-all duration-500 shadow-[0_0_12px_rgba(59,130,246,0.5)]"
                                    style={{ width: `${job.progress || 0}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {job.errorMessage && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-300 backdrop-blur-sm">
                            <div className="flex items-center gap-2 mb-1">
                                <AlertCircle className="w-4 h-4" />
                                <span className="font-bold uppercase tracking-wider text-xs">Error Log</span>
                            </div>
                            <p className="font-mono text-xs opacity-90">{job.errorMessage}</p>
                        </div>
                    )}

                    <div className="flex items-center justify-between text-xs font-medium text-slate-500 pt-2 border-t border-white/5">
                        <span className="flex items-center gap-1.5 ">
                            <Clock className="w-3.5 h-3.5" /> {formatDate(new Date(job._creationTime).toISOString())}
                        </span>
                        {job.retryCount > 0 && (
                            <span className="bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest text-[10px]">
                                Retry #{job.retryCount}
                            </span>
                        )}
                    </div>
                </div>

                <div className="ml-4 flex flex-col space-y-2">
                    {job.status === 'failed' && (
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => retryProcessingJob(job.jobId)}
                            disabled={job.retryCount >= 3}
                            title={job.retryCount >= 3 ? 'Maximum retries reached' : 'Retry processing'}
                        >
                            {job.retryCount >= 3 ? 'Max Retries' : 'Retry'}
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    )
}

const AdminProcessingTab = ({
    stats,
    jobsFilter,
    setJobsFilter,
    jobsLoading,
    loadProcessingJobs,
    paginatedJobs,
    filteredJobs,
    jobsPage,
    setJobsPage,
    jobsPerPage,
    setJobsPerPage,
    totalJobPages,
    retryProcessingJob,
    formatDate,
    extractVideoId
}) => {
    return (
        <div className="space-y-4">
            {/* Statistics Header */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 shadow-xl">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
                    <div className="p-4 bg-slate-900/40 rounded-xl border border-white/5">
                        <div className="text-3xl font-black text-white mb-1">{stats.total}</div>
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">Total Jobs</div>
                    </div>
                    <div className="p-4 bg-yellow-500/5 rounded-xl border border-yellow-500/10">
                        <div className="text-3xl font-black text-yellow-400 mb-1">{stats.pending}</div>
                        <div className="text-xs font-bold text-yellow-500/60 uppercase tracking-widest">Pending</div>
                    </div>
                    <div className="p-4 bg-blue-500/5 rounded-xl border border-blue-500/10">
                        <div className="text-3xl font-black text-blue-400 mb-1">{stats.processing}</div>
                        <div className="text-xs font-bold text-blue-500/60 uppercase tracking-widest">Active</div>
                    </div>
                    <div className="p-4 bg-emerald-500/5 rounded-xl border border-emerald-500/10">
                        <div className="text-3xl font-black text-emerald-400 mb-1">{stats.completed}</div>
                        <div className="text-xs font-bold text-emerald-500/60 uppercase tracking-widest">Done</div>
                    </div>
                    <div className="p-4 bg-red-500/5 rounded-xl border border-red-500/10">
                        <div className="text-3xl font-black text-red-400 mb-1">{stats.failed}</div>
                        <div className="text-xs font-bold text-red-500/60 uppercase tracking-widest">Failed</div>
                    </div>
                </div>
            </div>

            {/* Filter buttons */}
            <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                    {[
                        { key: 'all', label: 'All', count: stats.total },
                        { key: 'active', label: 'Active', count: stats.pending + stats.processing },
                        { key: 'completed', label: 'Completed', count: stats.completed },
                        { key: 'failed', label: 'Failed', count: stats.failed }
                    ].map(filter => (
                        <Button
                            key={filter.key}
                            type="button"
                            size="sm"
                            variant={jobsFilter === filter.key ? 'glass' : 'secondary'}
                            className="flex items-center space-x-2"
                            onClick={() => {
                                setJobsFilter(filter.key)
                                setJobsPage(1)
                            }}
                        >
                            <span className="capitalize">{filter.label}</span>
                            {filter.count > 0 && (
                                <span
                                    className={cn(
                                        'rounded-full px-1.5 py-0.5 text-xs',
                                        jobsFilter === filter.key ? 'bg-white/20 text-white' : 'bg-gray-300 text-gray-700'
                                    )}
                                >
                                    {filter.count}
                                </span>
                            )}
                        </Button>
                    ))}
                </div>

                <Button
                    type="button"
                    variant="glass"
                    onClick={loadProcessingJobs}
                    className="flex items-center space-x-2"
                >
                    <RefreshCw className="h-4 w-4" />
                    <span>Refresh</span>
                </Button>
            </div>

            {/* Jobs list */}
            {jobsLoading && (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading jobs...</p>
                </div>
            )}

            {!jobsLoading && paginatedJobs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No {jobsFilter !== 'all' ? jobsFilter : ''} jobs found</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {paginatedJobs.map((job) => (
                        <ProcessingJobCard
                            key={job.jobId}
                            job={job}
                            retryProcessingJob={retryProcessingJob}
                            formatDate={formatDate}
                            extractVideoId={extractVideoId}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {filteredJobs.length > jobsPerPage && (
                <Card className="flex items-center justify-between px-4 py-3 bg-white/5 border-white/10 backdrop-blur-sm">
                    <div className="flex items-center space-x-2">
                        <label className="text-sm text-slate-400">Show</label>
                        <select
                            value={jobsPerPage}
                            onChange={(e) => {
                                setJobsPerPage(Number(e.target.value))
                                setJobsPage(1)
                            }}
                            className="bg-white/10 border border-white/10 rounded px-3 py-1 text-sm text-white"
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span className="text-sm text-slate-400">per page</span>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setJobsPage(p => Math.max(1, p - 1))}
                            disabled={jobsPage === 1}
                            className="h-9 w-9 bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-30"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-white font-medium">
                            Page {jobsPage} of {totalJobPages || 1}
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setJobsPage(p => Math.min(totalJobPages, p + 1))}
                            disabled={jobsPage >= totalJobPages}
                            className="h-9 w-9 bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-30"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    )
}

export default AdminProcessingTab
